import { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { healthcareApi, RequestError, type InterviewRecordOptionsResponse } from '../api/client';

export type InterviewRecordOptionsState =
  | { status: 'loading'; options: null }
  | { status: 'ready'; options: InterviewRecordOptionsResponse }
  /** options API が無い古い BE（BE PR #143 の反映前）。面談記録の欄は出さない */
  | { status: 'missing'; options: null }
  | { status: 'error'; options: null; message: string };

/** 同じ画面の中で何度も取りに行かないよう、ログイン中の取得を使い回す（選択肢は画面を開いている間は変わらない） */
let cache: { token: string; promise: Promise<InterviewRecordOptionsResponse> } | null = null;

function fetchOptions(token: string): Promise<InterviewRecordOptionsResponse> {
  if (cache?.token === token) return cache.promise;
  const promise = healthcareApi.getInterviewRecordOptions(token);
  cache = { token, promise };
  // 失敗は使い回さない（次に開いたときに取り直す）
  promise.catch(() => {
    if (cache?.promise === promise) cache = null;
  });
  return promise;
}

/** テスト用: 使い回しを消す */
export function resetInterviewRecordOptionsCache(): void {
  cache = null;
}

/** ルートが無いとき（FastAPI 既定の 404。エラーコードが付かない） */
function isRouteMissing(err: unknown): boolean {
  return err instanceof RequestError && (err.status === 404 || err.status === 405) && !err.errorCode;
}

/**
 * 面談記録の項目・選択肢と、フォロー状況の 4 値・遷移（課題 #71 / BE PR #143）。
 */
export function useInterviewRecordOptions(): InterviewRecordOptionsState {
  const { getToken } = useAuth();
  const [state, setState] = useState<InterviewRecordOptionsState>({ status: 'loading', options: null });

  useEffect(() => {
    const token = getToken();
    if (!token) return;
    let cancelled = false;
    fetchOptions(token)
      .then((options) => {
        if (cancelled) return;
        // 形が合わない応答は、古い BE と同じ扱いにする（記録の欄を出さない）
        if (!options || !Array.isArray(options.items)) setState({ status: 'missing', options: null });
        else setState({ status: 'ready', options });
      })
      .catch((err) => {
        if (cancelled) return;
        if (isRouteMissing(err)) setState({ status: 'missing', options: null });
        else setState({ status: 'error', options: null, message: err instanceof Error ? err.message : '' });
      });
    return () => {
      cancelled = true;
    };
  }, [getToken]);

  return state;
}
